import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxSpinnerModule } from 'ngx-spinner';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '../../../shared/shared.module';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { DatePickerComponent } from '../../../shared/components/date-picker/date-picker.component';
import { CEtrTradeProvider } from '../../common/providers/c-etr-trade.provider';
import { numberValueFormatter } from '../../../shared/helpers/number-value-formatter';
import { OpenTradeBaseComponent } from '../open-trade-base.component';

@Component({
  standalone: true,
  selector: 'app-c-etr-trade',
  templateUrl: './c-etr-trade.component.html',
  styleUrls: ['./c-etr-trade.component.scss'],
  imports: [
    CommonModule,
    NgxSpinnerModule,
    ReactiveFormsModule,
    SharedModule,
    FormsModule,
    TooltipModule,
    DatePickerComponent,
  ],
  providers: [CEtrTradeProvider],
})
export class CEtrTradeComponent extends OpenTradeBaseComponent {
  readonly #cEtrTradeProvider = inject(CEtrTradeProvider);

  cEtrRSATradeForm: FormGroup;
  cEtrTradeForm: FormGroup;
  hasCreditor: string;

  protected override onInitialize(): void {
    this.hasCreditor = this.currentData?.creditorId;

    if (this.isEditTrade) {
      this.componentService.pageTitle.set('Edit a Trade');
      if (this.currentData.type === 'RSA') {
        this.detectExistRsa = false;
        this.isShowRsaForm = true;
        this.#cEtrTradeProvider.initRSAForm({
          ...this.currentData,
          creditorId: this.hasCreditor,
          type: 'RSA',
        });
        this.cEtrRSATradeForm = this.#cEtrTradeProvider.cEtrRSATradeForm;
      } else {
        this.detectExistRsa = false;
        this.isShowRsaForm = false;
        this.initCETRTradeForm(
          {
            ...this.currentData,
            faceValue:
              this.currentData.leftFaceValue || this.currentData.faceValue,
            rsaTradeId: this.currentData.tradeId,
          },
          true,
        );
      }
    }
  }

  protected override getMainForm(): FormGroup {
    return this.isShowRsaForm ? this.cEtrRSATradeForm : this.cEtrTradeForm;
  }

  protected override onCreateSuccess(response: any, data: any): void {
    if (response.type === 'RSA') {
      this.initCETRTradeForm(response);
    } else {
      this.closeModal();
    }
  }

  onSubmit(): void {
    if (this.cEtrRSATradeForm.invalid) return;
    const rawData = this.cEtrRSATradeForm.getRawValue();
    let formattedRawData = { ...rawData };
    formattedRawData.type = 'RSA';
    formattedRawData.debtor = rawData.debtor ? rawData.debtor : '';
    this.submitFormData(formattedRawData);
  }

  onSubmitETRTrade(): void {
    if (this.cEtrTradeForm.invalid) return;
    const rawData = this.cEtrTradeForm.getRawValue();
    let formattedRawData = { ...rawData };
    formattedRawData.type = this.type ? `${this.type}` : '';
    formattedRawData.edso = rawData.edso ? Number(rawData.edso) : null;
    formattedRawData.faceValue = rawData.faceValue
      ? Number(rawData.faceValue)
      : null;
    formattedRawData.deposit = rawData.deposit ? +rawData.deposit / 100 : null;
    delete formattedRawData.description;
    delete formattedRawData.pod;
    delete formattedRawData.rejectedMessage;

    this.spinnerService.setShowSpinner(true);
    this.currentData?.acquisitionId
      ? this.doUpdate(formattedRawData)
      : this.doCreate(formattedRawData);
  }

  selectCreditorForDetectRSA(event: any): void {
    if (!(event && event.id)) {
      this.detectExistRsa = true;
      return;
    }

    this.detectRSAForCreditor(
      event.id,
      (data) => this.initCETRTradeForm(data),
      () => {
        this.#cEtrTradeProvider.initRSAForm({
          creditorId: event.id,
          type: 'RSA',
        });
        this.cEtrRSATradeForm = this.#cEtrTradeProvider.cEtrRSATradeForm;
      },
    );
  }

  override fileChange(element: Event, acronym: string): void {
    super.fileChange(element, acronym, this.cEtrTradeForm);
  }

  get form2TradeDocumentsControl() {
    return this.getTradeDocumentsControl(this.cEtrTradeForm);
  }

  private initCETRTradeForm(data: any, isEdit?: boolean): void {
    this.cEtrRSATradeForm?.reset();
    this.isShowRsaForm = false;
    this.#cEtrTradeProvider.initTradeForm(data, isEdit);
    this.cEtrTradeForm = this.#cEtrTradeProvider.cEtrTradeForm;

    const selectedCredebtor =
      this.creditorList.find((creditor) => creditor.id === data.creditorId) ||
      this.debtorList.find((debtor) => debtor.id === data.debtorId);

    if (selectedCredebtor) {
      this.showTradeDocumentForEdit(
        selectedCredebtor,
        this.cEtrTradeForm,
        this.currentData?.documents,
      );
    }

    this.totalFaceValue = data.faceValue;
    this.remainingValue = numberValueFormatter({ value: data.faceValue });
    this.cEtrTradeForm.get('faceValue')?.valueChanges.subscribe((value) => {
      this.calculateRemainingValue(value);
    });

    this.subscribeToInvoiceDateChange(this.cEtrTradeForm);
    this.subscribeToPaymentTermsChange(this.cEtrTradeForm);
    this.subscribeToExpectedDateChange(this.cEtrTradeForm);
  }
}
